import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { luHandleAppiError, type U3134InitLU } from "./U3134Service";
import { U3134ContextProvider } from "./U3134Context";
import { useU3134InitLUHandlers } from "./U3134UseApiHandlers";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u3134";

export const U3134ROLES = {
  READ: "U3134:READ",
  ADD: "U3134:ADD",
  EDIT: "U3134:EDIT",
  DELETE: "U3134:DELETE",
  AUDITS: "U3134:AUDITS",
  GENERATE: "U3134:GENERATE",
  EXPORT: "U3134:EXPORT",
} as const;
export type U3134ROLES = (typeof U3134ROLES)[keyof typeof U3134ROLES];

const defaultInitLU: U3134InitLU = {
  launchSearch: false,
  hideSearch: false,
  nbRecordPerPage: 50,
  logicalUnitCode: "U3134",
};

const U3134: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const [initLUData, setInitLUData] = useState<U3134InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(["u3134"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU3134InitLUHandlers({
    onSuccess: (initLU: U3134InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        logicalUnitCode: initLU.logicalUnitCode ?? "U3134",
      });
      setInitLUReady(true);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné U3106
    onError: ({ error }: { error: any }) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné U3106

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t("title", { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3134ContextProvider initLU={initLUData}>
      <Outlet />
    </U3134ContextProvider>
  );
};

export default U3134;
